import React, { useEffect, useMemo, useState } from 'react';
import {
    Container, Box, Paper, Typography, TableContainer,
    Table, TableHead, TableRow, TableCell, TableBody, Link, Button,
    Stack, Snackbar, Alert, FormControlLabel, Switch, Grid, Skeleton
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import { ParentContact } from '../types';
import { logEvent } from '../analytics';
import { useSeason } from '../SeasonContext';
import { useRoster } from '../RosterContext';

const RosterPage = () => {
    const { selectedSeason } = useSeason();
    const seasonKey = selectedSeason.key;
    const { getRoster, isLoading, error: rosterError, isAuthReady } = useRoster();
    const roster = getRoster(seasonKey);
    const season = selectedSeason;
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [showSensitiveInfo, setShowSensitiveInfo] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

    useEffect(() => {
        if (season) {
            document.title = `Jets '14 | ${season.label} Roster`;
        } else {
            document.title = "Jets '14 | Roster";
        }
    }, [season]);

    const allEmails = useMemo(() => {
        if (!roster) return '';
        const emails = [
            ...roster.players.flatMap(p => p.parents.map(parent => parent.email).filter(Boolean)),
            ...roster.staff.map(s => s.email).filter(Boolean),
            ...roster.staff.map(s => s.secondaryEmail).filter(Boolean)
        ];
        return Array.from(new Set(emails.map(e => (e as string).trim()))).join(',');
    }, [roster]);

    const handleCopyEmails = () => {
        if (!allEmails) return;
        navigator.clipboard.writeText(allEmails).then(() => {
            setSnackbarMessage('All email addresses copied to clipboard!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            logEvent('copy_all_emails', { season: season?.label });
        }, () => {
            setSnackbarMessage('Failed to copy email addresses.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            logEvent('copy_all_emails_failed', { season: season?.label });
        });
    };

    const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    if ((isLoading || !isAuthReady) && !roster) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ my: 4 }}>
                    <Skeleton variant="text" width="40%" height={48} sx={{ mb: 2 }} />
                    <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2, mb: 4 }} />
                    <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2 }} />
                </Box>
            </Container>
        );
    }

    if (!roster) {
        return (
            <Container maxWidth="lg">
                <Box sx={{ my: 4 }}>
                    <Typography variant="h4" component="h1" gutterBottom>Roster Not Found</Typography>
                    <Typography color="text.secondary">
                        {rosterError || 'No roster data available for the selected season.'}
                    </Typography>
                </Box>
            </Container>
        );
    }

    const ParentInfo = ({ parent }: { parent: ParentContact }) => (
        <Box>
            <Typography variant="body2">{parent.name}</Typography>
            {parent.phone && <Typography variant="body2" color="text.secondary"><Link href={`tel:${parent.phone}`}>{parent.phone}</Link></Typography>}
            {parent.email && <Typography variant="body2" color="text.secondary"><Link href={`mailto:${parent.email}`}>{parent.email}</Link></Typography>}
        </Box>
    );

    return (
        <Container maxWidth="xl">
            <Box sx={{ my: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {season?.label} Roster & Contacts
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            startIcon={<ContentCopyIcon />}
                            onClick={handleCopyEmails}
                            disabled={!allEmails}
                        >
                            Copy All Emails
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<EmailIcon />}
                            component={Link}
                            href={`mailto:${allEmails}`}
                            disabled={!allEmails}
                            onClick={() => logEvent('email_all', { season: season?.label })}
                        >
                            Email All
                        </Button>
                    </Stack>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showSensitiveInfo}
                                onChange={(e) => {
                                    setShowSensitiveInfo(e.target.checked);
                                    logEvent('toggle_sensitive_info', { show: e.target.checked, season: season?.label });
                                }}
                            />
                        }
                        label="Show DOB & USAH #"
                        sx={{ pt: { xs: 1, sm: 0 } }}
                    />
                </Stack>

                {/* Players Table */}
                <Paper variant="outlined" sx={{ mb: 4, borderRadius: 2 }}>
                    <TableContainer>
                        <Table aria-label="player roster" size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: 'grey.200' }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Name</TableCell>
                                    {showSensitiveInfo && <TableCell>DOB</TableCell>}
                                    {showSensitiveInfo && <TableCell>USAH #</TableCell>}
                                    <TableCell>Parents</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[...roster.players].sort((a, b) => a.lastName.localeCompare(b.lastName)).map((player) => (
                                    <TableRow key={`${player.lastName}-${player.firstName}`} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell>{player.jersey !== '' && player.jersey !== undefined && player.jersey !== null ? player.jersey : '-'}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {`${player.lastName}, ${player.firstName}${player.nickname ? ` "${player.nickname}"` : ''}`}
                                            </Typography>
                                        </TableCell>
                                        {showSensitiveInfo && <TableCell>{player.dob || '-'}</TableCell>}
                                        {showSensitiveInfo && <TableCell>{player.usah || '-'}</TableCell>}
                                        <TableCell>
                                            <Grid container spacing={{ xs: 1, sm: 1}} alignItems="flex-start">
                                                {player.parents.map((parent, index) => (
                                                    parent && (
                                                        <Grid key={index} size={{ xs: 12, sm: 6}}>
                                                            <ParentInfo parent={parent} />
                                                        </Grid>
                                                    )
                                                ))}
                                            </Grid>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Staff Table */}
                <Typography variant="h5" component="h2" gutterBottom>
                    Staff
                </Typography>
                <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                    <TableContainer>
                        <Table aria-label="staff roster" size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: 'grey.200' }}>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>Contact</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {roster.staff.map((staffMember, index) => (
                                    <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {`${staffMember.lastName}, ${staffMember.firstName}`}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{staffMember.role}</TableCell>
                                        <TableCell>
                                            {staffMember.phone && <Typography variant="body2"><Link href={`tel:${staffMember.phone}`}>{staffMember.phone}</Link></Typography>}
                                            {staffMember.email && <Typography variant="body2"><Link href={`mailto:${staffMember.email}`}>{staffMember.email}</Link></Typography>}
                                            {staffMember.secondaryEmail && <Typography variant="body2"><Link href={`mailto:${staffMember.secondaryEmail}`}>{staffMember.secondaryEmail}</Link></Typography>}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
            <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={handleSnackbarClose}>
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default RosterPage;